import React, { useState, useEffect, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';
import { 
    collection, 
    doc, 
    addDoc, 
    deleteDoc, 
    onSnapshot, 
    query, 
    orderBy, 
    getDocs, 
    writeBatch,
    Timestamp 
} from 'firebase/firestore';
import { 
    Favicon, 
    SendIcon, 
    TrashIcon,
    InfoIcon,
    ChevronDownIcon,
    MicrophoneIcon // <-- 1. Import new icon
} from '../../components/icons/Icons';

/**
 * Helper function to find and parse JSON that the AI sends back.
 */
const parseTaskJson = (text) => {
  // This regex finds a JSON block and captures the object inside
  const jsonMatch = text.match(/```(?:json)?\s*({[\s\S]*?})\s*```/);
  if (jsonMatch && jsonMatch[1]) {
    try {
      // We found a JSON block, so we parse it
      return JSON.parse(jsonMatch[1]);
    } catch (e) {
      console.error("Failed to parse JSON from AI response:", e);
      return null;
    }
  }
  // No JSON block was found
  return null;
};

/**
 * Quick Reference Component (Updated for expanded scope)
 */
const QuickReference = () => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="mb-4 bg-white rounded-lg shadow border">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex justify-between items-center w-full p-4"
            >
                <div className="flex items-center">
                    <InfoIcon className="h-5 w-5 text-[#20BEC6] mr-2" />
                    <span className="font-bold text-[#213368]">Coach's Quick Guide</span>
                </div>
                <ChevronDownIcon className={`h-5 w-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
                <div className="p-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600 mb-2">
                        I can help with a range of topics. Ask me about:
                    </p>
                    <ul className="list-disc pl-5 space-y-2 text-sm">
                        <li>
                            <strong className="text-gray-700">Time Management:</strong>
                            <span className="block text-gray-500">e.g., "Help me prioritize my tasks."</span>
                        </li>
                        <li>
                            <strong className="text-gray-700">Team Management:</strong>
                            <span className="block text-gray-500">e.g., "How can I better motivate my team?"</span>
                        </li>
                        <li>
                            <strong className="text-gray-700">Managing Up:</strong>
                            <span className="block text-gray-500">e.g., "How do I ask my manager for a resource?"</span>
                        </li>
                        <li>
                            <strong className="text-gray-700">General Advice:</strong>
                            <span className="block text-gray-500">e.g., "I have a conflict with a coworker."</span>
                        </li>
                    </ul>
                </div>
            )}
        </div>
    );
};


/**
 * Main component for the AI Time Coach
 */
export default function TimeCoachModule({ userProfile, appId }) {
    
    const [messages, setMessages] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const chatEndRef = useRef(null);
    const inputRef = useRef(null);

    // --- 2. State for Voice Input ---
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef(null); // To hold the SpeechRecognition instance

    const userRootRef = doc(db, `/artifacts/${appId}/users/${userProfile.uid}`);
    const historyCollectionRef = collection(userRootRef, 'aiCoachHistory');
    const tasksCollectionRef = collection(userRootRef, 'aiCoachTasks');

    // --- 3. Setup Speech Recognition ---
    useEffect(() => {
        // Check for browser support
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            console.warn("Speech recognition not supported in this browser.");
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.continuous = false; // Stop after first phrase
        recognition.interimResults = false; // Get final results
        recognition.lang = 'en-US';

        // Event: When speech is recognized
        recognition.onresult = (event) => {
            const transcript = event.results[0][0].transcript;
            setNewMessage(transcript); // Set the input field with the transcript
        };

        // Event: When speech recognition ends
        recognition.onend = () => {
            setIsListening(false);
            inputRef.current?.focus(); // Re-focus input after listening
        };

        // Event: Handle errors
        recognition.onerror = (event) => {
            console.error("Speech recognition error:", event.error);
            setIsListening(false);
        };

        // Save the instance to the ref
        recognitionRef.current = recognition;

    }, []);

    // Load chat messages
    useEffect(() => {
        const q = query(historyCollectionRef, orderBy("timestamp"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const loadedMessages = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setMessages(loadedMessages);

            if (loadedMessages.length === 0 && !isLoading) {
                 setIsLoading(true);
                 handleSendMessage(null, true);
            }
        }, (error) => {
            console.error("Error listening to chat history:", error);
        });

        return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // This runs once on mount

    // Load prioritized tasks
    useEffect(() => {
        const q = query(tasksCollectionRef, orderBy("createdAt", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const loadedTasks = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setTasks(loadedTasks);
        }, (error) => {
            console.error("Error listening to tasks:", error);
        });

        return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // This effect scrolls the chat window down
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // This effect handles the auto-focus of the input
    useEffect(() => {
        if (!isLoading) {
            inputRef.current?.focus();
        }
    }, [isLoading]);

    // --- 4. New Function to Toggle Listening ---
    const handleToggleListening = () => {
        if (!recognitionRef.current) {
             // Use a simple alert if the API isn't supported
             alert("Voice recognition is not supported in this browser.");
             return;
        }

        if (isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
        } else {
            try {
                // Request microphone permission and start
                recognitionRef.current.start();
                setIsListening(true);
            } catch (e) {
                console.error("Could not start recognition:", e);
                // This can happen if permission is not granted
                alert("Could not start voice recognition. Please allow microphone access.");
            }
        }
    };


    const handleSendMessage = async (event, isIntro = false) => {
        if (event) event.preventDefault();
        
        const messageToSend = isIntro ? "Hello" : newMessage;
        if (!messageToSend && !isIntro) return;

        // Stop listening if we're sending a message
        if (isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
        }

        setIsLoading(true);
        
        if (!isIntro) {
            try {
                await addDoc(historyCollectionRef, {
                    role: 'user',
                    content: messageToSend,
                    timestamp: Timestamp.now()
                });
            } catch (error) {
                console.error("Error saving user message:", error);
                setIsLoading(false);
                return;
            }
        }
        setNewMessage(''); 

        const history = messages.map(msg => ({
            role: msg.role,
            parts: [{ text: msg.content }],
        }));

        try {
            const functions = getFunctions();
            const chatWithCoach = httpsCallable(functions, 'chatWithCoach');
            
            const result = await chatWithCoach({ 
                history: history, 
                userMessage: messageToSend 
            });
            
            const aiResponseText = result.data.responseText;

            // --- THIS IS THE NEW LOGIC FOR POINT 2 ---
            // Check if the AI's response is *only* JSON
            const taskData = parseTaskJson(aiResponseText);
            
            if (taskData) {
                // It's JSON! This means the user said "yes".
                // 1. Add the task to the list
                await addDoc(tasksCollectionRef, {
                    ...taskData,
                    createdAt: Timestamp.now()
                });
                // 2. Add a *friendly* confirmation message to the chat
                await addDoc(historyCollectionRef, {
                    role: 'model',
                    content: `Okay, I've added "${taskData.task}" to your list. What's next?`,
                    timestamp: Timestamp.now()
                });

            } else {
                // It's a normal text response, add it to chat
                await addDoc(historyCollectionRef, {
                    role: 'model',
                    content: aiResponseText,
                    timestamp: Timestamp.now()
                });
            }

        } catch (error) {
            console.error("Error calling chatWithCoach:", error);
            try {
                await addDoc(historyCollectionRef, {
                    role: 'model',
                    content: "Sorry, I ran into an error. Please try again.",
                    timestamp: Timestamp.now()
                });
            } catch (e) {
                console.error("Error saving error message:", e);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteTask = async (taskId) => {
        try {
            const taskDocRef = doc(tasksCollectionRef, taskId);
            await deleteDoc(taskDocRef);
        } catch (error) {
            console.error("Error deleting task: ", error);
        }
    };

    const handleClearAll = async () => {
        if (window.confirm("Are you sure you want to clear your entire chat history and all prioritized tasks? This cannot be undone.")) {
            setIsLoading(true);
            try {
                const batch = writeBatch(db);

                const historySnapshot = await getDocs(historyCollectionRef);
                historySnapshot.docs.forEach(doc => {
                    batch.delete(doc.ref);
                });

                const tasksSnapshot = await getDocs(tasksCollectionRef);
                tasksSnapshot.docs.forEach(doc => {
                    batch.delete(doc.ref);
                });

                await batch.commit();
            } catch (error) {
                console.error("Error clearing history: ", error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const priorityStyles = {
        'Do': 'bg-red-100 text-red-800 border-red-300',
        'Schedule': 'bg-blue-100 text-blue-800 border-blue-300',
        'Delegate': 'bg-yellow-100 text-yellow-800 border-yellow-300',
        'Eliminate': 'bg-gray-100 text-gray-800 border-gray-300',
    };

    return (
        <div className="flex space-x-6 h-[80vh]">
            
            {/* Left Panel: Chat Interface (w-2/3) */}
            <div className="flex flex-col w-2/3 h-full bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4 border-b pb-4">
                    <Favicon className="h-10 w-10" />
                    <h1 className="text-2xl font-bold text-[#213368] ml-3">AI Work Coach</h1>
                </div>

                {/* Chat Message Area */}
                <div className="flex-1 overflow-y-auto pr-2 space-y-4">
                    {messages.map((msg, index) => (
                        <div key={msg.id || index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`p-3 rounded-lg max-w-lg ${msg.role === 'user' ? 'bg-[#20BEC6] text-white' : 'bg-gray-100 text-gray-800'}`}>
                                {msg.content.split('\n').map((line, i) => (
                                    <p key={i}>{line}</p>
                                ))}
                            </div>
                        </div>
                    ))}

                    {/* Loader */}
                    {isLoading && messages.length > 0 && (
                        <div className="flex justify-start">
                            <div className="p-3 rounded-lg max-w-lg bg-gray-100 text-gray-600 italic">
                                Coach is thinking...
                            </div>
                        </div>
                    )}
                    <div ref={chatEndRef} />
                </div>

                {/* --- 5. Updated Chat Input Form --- */}
                <form onSubmit={handleSendMessage} className="mt-4 flex items-center space-x-2">
                    <input
                        ref={inputRef}
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder={isListening ? "Listening..." : "Type or click the mic to talk..."}
                        className="w-full p-3 border border-gray-300 rounded-lg shadow-sm"
                        disabled={isLoading}
                    />
                    
                    {/* Voice Input Button */}
                    <button
                        type="button" // Important: type="button" to prevent form submission
                        onClick={handleToggleListening}
                        disabled={isLoading}
                        className={`p-3 rounded-lg shadow-md transition-colors ${
                            isListening 
                                ? 'bg-red-500 text-white' // Active listening state
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        } disabled:bg-gray-400`}
                        title={isListening ? "Stop listening" : "Start listening"}
                    >
                        <MicrophoneIcon />
                    </button>

                    {/* Send Button */}
                    <button
                        type="submit"
                        disabled={isLoading || !newMessage}
                        className="p-3 bg-[#20BEC6] text-white font-semibold rounded-lg shadow-md hover:bg-opacity-99 disabled:bg-gray-400"
                    >
                        <SendIcon /> 
                    </button>
                </form>
            </div>

            {/* Right Panel: Prioritized Task List (w-1/3) */}
            <div className="w-1/3 h-full bg-gray-50 p-6 rounded-lg shadow-md flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-[#213368]">Your Prioritized List</h2>
                    {(tasks.length > 0 || messages.length > 1) && (
                        <button 
                            onClick={handleClearAll} 
                            className="flex items-center px-2 py-1 text-xs text-red-600 bg-red-100 rounded-md hover:bg-red-200"
                            title="Clear all tasks and chat history"
                            disabled={isLoading}
                        >
                            <TrashIcon className="h-4 w-4 mr-1" />
                            Clear All
                        </button>
                    )}
                </div>
                
                <QuickReference />

                <div className="flex-1 overflow-y-auto mt-4">
                    {tasks.length > 0 ? (
                        <ul className="space-y-3">
                            {tasks.map(task => (
                                <li key={task.id} className="p-4 bg-white rounded-lg shadow border group relative">
                                    <div className="flex justify-between items-start mb-2">
                                        <p className="font-bold text-gray-800 break-words pr-10">{task.task}</p>
                                        <span className={`ml-2 flex-shrink-0 px-3 py-1 text-sm font-medium rounded-full border ${priorityStyles[task.priority] || 'bg-gray-100'}`}>
                                            {task.priority}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-600 break-words">{task.reasoning}</p>
                                    
                                    <button 
                                        onClick={() => handleDeleteTask(task.id)}
                                        className="absolute top-2 right-2 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Delete this task"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-center text-gray-500 py-8">Your prioritized tasks will appear here as you discuss them with the coach.</p>
                    )}
                </div>

                {/* Disclaimer */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500 italic">
                        Disclaimer: This AI coach provides suggestions based on
                        your input. You are responsible for finalizing your
                        tasks. Please consult your manager for any
                        re-prioritization that requires approval.
                    </p>
                </div>
            </div>
        </div>
    );
}